import {
  AnimatedSprite,
  Application,
  Assets,
  Cache,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  Texture,
} from "pixi.js";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import {
  BattlePokemonData,
  Direction,
  PokemonType,
} from "types/battle-engine/battle";
import {
  GRID_WIDTH,
  GRID_HEIGHT,
  gridTileSize,
  gridMarginX,
  gridMarginY,
} from "types/battle-engine/constants";
import {
  RaidRoomOption,
  RaidRoomConfig,
  RaidBossConfig,
} from "types/raids/rooms";
import {
  getEnemySpawnPositions,
  getPlayerSpawnPositions,
} from "@/lib/battle/utils/collision-data";
import { RaidPokemon, SlotConfig, PlayerRole } from "types/raid-dynamodb";
import { VFX_SPRITE_FRAME_COUNT_DATA } from "@/data/vfx-sprite-frame-count-data";
import { CompleteBattleLog } from "types/battle-engine";

/** Pokemon with its assigned slot index for correct positioning */
export interface PokemonWithSlot {
  pokemon: RaidPokemon;
  slotIndex: number;
}

/** Sparkle VFX sprite path */
const SPAWN_VFX_PATH = "/assets/spells/self/self_sparkles_003__26.png";
/** Healing VFX sprite path */
const HEAL_VFX_PATH = "/assets/spells/vfx/self_sparkles_006__50.png";

// Battle map constants - must match Raid.tsx battle setup
const BATTLE_MAP_SCALE = 0.75;
const BATTLE_GRID_WIDTH = GRID_WIDTH * gridTileSize;
const BATTLE_GRID_HEIGHT = GRID_HEIGHT * gridTileSize;
const BATTLE_MAP_CENTER_X = gridMarginX + BATTLE_GRID_WIDTH / 2;
const BATTLE_MAP_CENTER_Y = gridMarginY + BATTLE_GRID_HEIGHT / 2;

export interface RoomSectionBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RoomSectionConfig {
  /** Full screen height - needed to calculate battle-scale positioning */
  screenHeight: number;
  /** Full screen width */
  screenWidth: number;
}

/** Positioning mode for the map */
type MapPositionMode = "preview" | "battle";

/**
 * Represents a single room option section in the PIXI room selection view.
 * Contains a masked map background and enemy Pokemon sprites.
 *
 * Map rendering strategy:
 * - Map is rendered at battle scale (0.65) and positioned at battle grid center
 * - A mask clips the visible portion (top 1/3 during selection, full during expansion)
 * - During expansion, only the mask grows - map scale/position stays constant
 */
export class RoomSection {
  public container: Container;
  public mapSprite: Sprite | null = null;
  public mask: Graphics;
  public pokemons: ClientPokemon[] = [];
  public playerPokemons: ClientPokemon[] = [];
  public bounds: RoomSectionBounds;
  public roomOption: RaidRoomOption;
  public index: number;

  private app: Application;
  private pokemonContainer: Container;
  private playerPokemonContainer: Container;
  private slotPlaceholderContainer: Container;
  private isDestroyed = false;
  private config: RoomSectionConfig;
  private positionMode: MapPositionMode = "preview";
  /** Track player pokemon by slot index for incremental updates */
  private playerPokemonBySlot: Map<
    number,
    { pokemon: ClientPokemon; pokemonId: number }
  > = new Map();
  /** Total number of slots for positioning calculation */
  private totalSlots: number = 0;

  constructor(
    app: Application,
    roomOption: RaidRoomOption,
    index: number,
    bounds: RoomSectionBounds,
    config: RoomSectionConfig,
  ) {
    this.app = app;
    this.roomOption = roomOption;
    this.index = index;
    this.bounds = bounds;
    this.config = config;

    // Create main container for this section
    this.container = new Container();
    this.container.sortableChildren = true;
    this.container.x = bounds.x;
    this.container.y = bounds.y;

    // Create mask for clipping - defer actual drawing to avoid geometry issues
    this.mask = new Graphics();
    // Don't add mask as child yet - will be drawn and applied after content loads

    // Create pokemon container (will be positioned based on grid)
    this.pokemonContainer = new Container();
    this.pokemonContainer.sortableChildren = true;
    this.container.addChild(this.pokemonContainer);

    // Create slot placeholder container (positioned at bottom, below pokemon)
    this.slotPlaceholderContainer = new Container();
    this.slotPlaceholderContainer.sortableChildren = true;
    this.slotPlaceholderContainer.zIndex = -10; // Below pokemon
    this.container.addChild(this.slotPlaceholderContainer);

    // Create player pokemon container (positioned at bottom of visible area)
    this.playerPokemonContainer = new Container();
    this.playerPokemonContainer.sortableChildren = true;
    this.playerPokemonContainer.zIndex = 0; // Above slot placeholders
    this.container.addChild(this.playerPokemonContainer);
  }

  /**
   * Draw the mask graphics (separated to avoid geometry issues)
   */
  private drawMask(): void {
    this.mask.clear();
    this.mask.rect(0, 0, this.bounds.width, this.bounds.height);
    this.mask.fill({ color: 0xffffff });
  }

  /**
   * Apply the mask to the container (call after content is loaded)
   */
  applyMask(): void {
    this.drawMask();
    this.container.addChild(this.mask);
    this.container.mask = this.mask;
  }

  /**
   * Calculate the scale factor to fit battle grid to screen width.
   * This matches the world container scaling in Raid.tsx.
   */
  private getBattleScale(): number {
    const scaleX = this.config.screenWidth / BATTLE_GRID_WIDTH;
    const scaleY = this.config.screenHeight / BATTLE_GRID_HEIGHT;
    return Math.min(scaleX, scaleY, 1);
  }

  /**
   * Calculate map position in section-local coordinates for BATTLE mode.
   * The map is positioned at battle grid center, then offset for this section's row.
   */
  private getBattleMapPosition(): { x: number; y: number } {
    const battleScale = this.getBattleScale();

    // Battle grid center in screen coordinates (when world is at origin)
    // The world container in battle centers the grid on screen
    const screenCenterX = this.config.screenWidth / 2;
    const screenCenterY = this.config.screenHeight / 2;

    // Map center in world coordinates
    const mapCenterInWorld = {
      x: BATTLE_MAP_CENTER_X,
      y: BATTLE_MAP_CENTER_Y,
    };

    // Grid center in world coordinates
    const gridCenterInWorld = {
      x: gridMarginX + BATTLE_GRID_WIDTH / 2,
      y: gridMarginY + BATTLE_GRID_HEIGHT / 2,
    };

    // Convert map position to screen coordinates
    // world.position = screenCenter - gridCenter * scale
    // mapScreenPos = world.position + mapWorldPos * scale
    // = screenCenter - gridCenter * scale + mapWorldPos * scale
    // = screenCenter + (mapWorldPos - gridCenter) * scale
    const mapScreenX =
      screenCenterX + (mapCenterInWorld.x - gridCenterInWorld.x) * battleScale;
    const mapScreenY =
      screenCenterY + (mapCenterInWorld.y - gridCenterInWorld.y) * battleScale;

    // Convert to section-local coordinates (subtract section's Y offset)
    return {
      x: mapScreenX - this.bounds.x,
      y: mapScreenY - this.bounds.y,
    };
  }

  /**
   * Calculate map position in section-local coordinates for PREVIEW mode.
   * In preview mode, we show a portion of the map that contains the enemy spawn area.
   * Instead of showing the exact top 1/3 (0%-33%), we show an offset portion (~10%-43%)
   * to better capture where enemies typically spawn.
   */
  private getPreviewMapPosition(): { x: number; y: number } {
    const effectiveScale = this.getEffectiveMapScale();

    // Map dimensions
    const mapHeight = this.mapSprite?.texture?.height ?? 1000;
    const scaledMapHeight = mapHeight * effectiveScale;
    const scaledMapHalfHeight = scaledMapHeight / 2;

    // Offset: shift the view down by ~x% of map height to show enemy spawn area
    // This means instead of showing top edge at Y=0, we show from ~x% down
    const verticalOffsetPercent = 0.35;
    const verticalOffset = scaledMapHeight * verticalOffsetPercent;

    // Map center Y: position so the visible portion starts at verticalOffset
    // If map top were at Y=0, center would be at scaledMapHalfHeight
    // To shift view down by verticalOffset, we subtract it from the center Y
    const mapCenterY = scaledMapHalfHeight - verticalOffset;

    return {
      x: this.bounds.width / 2,
      y: mapCenterY,
    };
  }

  /**
   * Get current map position based on position mode
   */
  private getMapPosition(): { x: number; y: number } {
    if (this.positionMode === "preview") {
      return this.getPreviewMapPosition();
    }
    return this.getBattleMapPosition();
  }

  /**
   * Get the effective map scale (battle scale * world scale)
   */
  private getEffectiveMapScale(): number {
    return BATTLE_MAP_SCALE * this.getBattleScale();
  }

  /**
   * Load and setup the map background sprite.
   * Map is rendered at battle scale. Initial position depends on position mode:
   * - preview: map top aligned with section top (fills section with content)
   * - battle: map centered on battle grid (with margins)
   */
  async loadMap(): Promise<void> {
    if (this.isDestroyed) return;

    try {
      const mapPath = `/assets/maps/${this.roomOption.mapId}.webp`;
      const texture = await Assets.load(mapPath);

      if (this.isDestroyed) return;
      if (!texture) return;

      this.mapSprite = new Sprite(texture);
      this.mapSprite.anchor.set(0.5);

      // Use battle-consistent scale (same scale in both modes)
      const effectiveScale = this.getEffectiveMapScale();
      this.mapSprite.scale.set(effectiveScale);

      // Position map based on current mode (preview or battle)
      const mapPos = this.getMapPosition();
      this.mapSprite.x = mapPos.x;
      this.mapSprite.y = mapPos.y;
      this.mapSprite.zIndex = -1000;

      this.container.addChildAt(this.mapSprite, 0);
    } catch (error) {
      // Map loading failed silently
    }
  }

  /**
   * Spawn enemy Pokemon for this room.
   * Pokemon are positioned at battle-scale coordinates to match the map.
   */
  async spawnEnemies(): Promise<void> {
    if (this.isDestroyed) return;

    const enemies = this.getEnemies();
    if (enemies.length === 0) return;

    // Get spawn positions from collision data (top-left-most positions)
    const spawnPositions = getEnemySpawnPositions(
      this.roomOption.mapId,
      enemies.length,
    );

    // Position pokemon container at battle grid center (same as map)
    const battleScale = this.getBattleScale();
    const mapPos = this.getMapPosition();
    this.pokemonContainer.scale.set(battleScale);
    this.pokemonContainer.x = mapPos.x;
    this.pokemonContainer.y = mapPos.y;

    // Calculate spacing between Pokemon (in world units, will be scaled by container)
    const spacingX = 60;

    // Spawn each enemy at collision-based positions (top-left-most first)
    const spawnPromises = enemies.map(async (enemy, i) => {
      if (this.isDestroyed) return;

      try {
        // Position Pokemon in a row centered in the section
        // Use grid position 0,0 as base, then manually offset the container position
        const basePosition = spawnPositions[i] ?? { x: 0, y: 0 };

        const battleData: BattlePokemonData = {
          id: 90000 + this.index * 100 + i,
          base_pokemon_name: enemy.name,
          name: enemy.name,
          level: enemy.level,
          pokemon_types: [PokemonType.NORMAL],
          hp: 100,
          max_hp: 100,
          ability_power: 10,
          resistance: 10,
          speed: 10,
          skills: [],
          scale: 1,
        };

        // Create Pokemon at grid position 0,0 - we'll manually position the container
        const pokemon = new ClientPokemon(
          battleData,
          { x: 0, y: 0 },
          "B",
          this.app,
          this.pokemonContainer,
          null,
          false, // hideUI
        );

        await pokemon.load();

        if (this.isDestroyed) {
          try {
            (pokemon as any).container?.destroy({ children: true });
          } catch {
            // Cleanup failed silently
          }
          return;
        }

        // Manually position the Pokemon container in a centered row
        // Offset from center based on index
        const totalWidth = (enemies.length - 1) * spacingX;
        const startX = -totalWidth / 2;
        const pokemonContainer = (pokemon as any).container;
        if (pokemonContainer) {
          pokemonContainer.x = startX + i * spacingX;
          pokemonContainer.y = 0;
        }

        // Set idle animation facing South
        pokemon.setAnimation(["Idle"], Direction.S);
        this.pokemons.push(pokemon);
      } catch {
        // Enemy spawn failed silently
      }
    });

    await Promise.all(spawnPromises);
  }

  /**
   * Get enemies from room option (handles both room and boss types)
   */
  private getEnemies(): { name: string; level: number }[] {
    if (this.roomOption.kind === "boss") {
      const bossRoom = this.roomOption as RaidBossConfig;
      return [bossRoom.boss, ...(bossRoom.minions || [])];
    } else {
      const room = this.roomOption as RaidRoomConfig;
      return room.enemies;
    }
  }

  /**
   * Update player Pokemon (Team A) for this room with incremental changes.
   * Only adds new pokemon and removes ones no longer in the list.
   * Positions pokemon at their assigned slot position.
   *
   * @param pokemonsWithSlots Array of pokemon with their slot indices
   * @param totalSlots Total number of slots for positioning calculation
   */
  async updatePlayerPokemon(
    pokemonsWithSlots: PokemonWithSlot[],
    totalSlots: number,
  ): Promise<void> {
    if (this.isDestroyed) return;

    this.totalSlots = totalSlots;

    // Position player pokemon container at bottom portion of visible area
    const battleScale = this.getBattleScale();
    const mapPos = this.getMapPosition();
    this.playerPokemonContainer.scale.set(battleScale);
    this.playerPokemonContainer.x = mapPos.x;
    this.playerPokemonContainer.y = this.bounds.height * 0.65;

    // Build a set of current pokemon IDs by slot
    const newPokemonBySlot = new Map<number, RaidPokemon>();
    for (const { pokemon, slotIndex } of pokemonsWithSlots) {
      newPokemonBySlot.set(slotIndex, pokemon);
    }

    // Remove pokemon that are no longer in any slot
    const slotsToRemove: number[] = [];
    for (const [slotIndex, { pokemon, pokemonId }] of this
      .playerPokemonBySlot) {
      const newPokemon = newPokemonBySlot.get(slotIndex);
      if (!newPokemon || newPokemon.id !== pokemonId) {
        // Pokemon changed or removed from this slot
        slotsToRemove.push(slotIndex);
        try {
          const container = (pokemon as any).container;
          if (container && !container.destroyed) {
            container.destroy({ children: true });
          }
        } catch {
          // Cleanup failed silently
        }
        // Remove from playerPokemons array
        const idx = this.playerPokemons.indexOf(pokemon);
        if (idx >= 0) this.playerPokemons.splice(idx, 1);
      }
    }
    for (const slotIndex of slotsToRemove) {
      this.playerPokemonBySlot.delete(slotIndex);
    }

    // Add pokemon that are new
    const spacingX = 60;
    const spawnPromises: Promise<void>[] = [];

    for (const { pokemon, slotIndex } of pokemonsWithSlots) {
      const existing = this.playerPokemonBySlot.get(slotIndex);
      if (existing && existing.pokemonId === pokemon.id) {
        // Already exists at this slot, skip
        continue;
      }

      // Spawn new pokemon at this slot
      spawnPromises.push(
        this.spawnSinglePlayerPokemon(pokemon, slotIndex, totalSlots, spacingX),
      );
    }

    await Promise.all(spawnPromises);
  }

  /**
   * Spawn a single player pokemon at a specific slot position with VFX.
   */
  private async spawnSinglePlayerPokemon(
    pokemon: RaidPokemon,
    slotIndex: number,
    totalSlots: number,
    spacingX: number,
  ): Promise<void> {
    if (this.isDestroyed) return;

    try {
      const battleData: BattlePokemonData = {
        id: 80000 + this.index * 100 + slotIndex,
        base_pokemon_name: pokemon.base_pokemon_name,
        name: pokemon.base_pokemon_name,
        level: pokemon.level,
        pokemon_types: [PokemonType.NORMAL],
        hp: pokemon.current_hp ?? pokemon.max_hp ?? 100,
        max_hp: pokemon.max_hp ?? 100,
        ability_power: 10,
        resistance: 10,
        speed: 10,
        skills: [],
        scale: 1,
      };

      const clientPokemon = new ClientPokemon(
        battleData,
        { x: 0, y: 0 },
        "A",
        this.app,
        this.playerPokemonContainer,
        null,
        false,
      );

      await clientPokemon.load();

      if (this.isDestroyed) {
        try {
          (clientPokemon as any).container?.destroy({ children: true });
        } catch {
          // Cleanup failed silently
        }
        return;
      }

      // Position at the correct slot (layout based on totalSlots, not current count)
      const totalWidth = (totalSlots - 1) * spacingX;
      const startX = -totalWidth / 2;
      const pokemonContainer = (clientPokemon as any).container;
      if (pokemonContainer) {
        pokemonContainer.x = startX + slotIndex * spacingX;
        pokemonContainer.y = 0;
      }

      // Set idle animation facing North (toward enemies)
      clientPokemon.setAnimation(["Idle"], Direction.N);

      // Track in both structures
      this.playerPokemons.push(clientPokemon);
      this.playerPokemonBySlot.set(slotIndex, {
        pokemon: clientPokemon,
        pokemonId: pokemon.id,
      });

      // Play spawn VFX
      this.playSpawnVFX(startX + slotIndex * spacingX, 0);
    } catch {
      // Player spawn failed silently
    }
  }

  /**
   * Play sparkle VFX at the given position (relative to playerPokemonContainer).
   */
  private async playSpawnVFX(x: number, y: number): Promise<void> {
    try {
      const texture = Cache.has(SPAWN_VFX_PATH)
        ? Cache.get(SPAWN_VFX_PATH)
        : await Assets.load<Texture>(SPAWN_VFX_PATH);

      const frameCount = VFX_SPRITE_FRAME_COUNT_DATA[SPAWN_VFX_PATH];
      if (!frameCount) return;

      const frames: Texture[] = [];
      const frameWidth = texture.width / frameCount;
      const frameHeight = texture.height;

      for (let i = 0; i < frameCount; i++) {
        const cacheKey = SPAWN_VFX_PATH + "__frame-" + i;
        if (Cache.has(cacheKey)) {
          frames.push(Cache.get(cacheKey));
        } else {
          const frame = new Texture({
            source: texture.source,
            frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight),
          });
          Cache.set(cacheKey, frame);
          frames.push(frame);
        }
      }

      const vfxSprite = new AnimatedSprite(frames);
      vfxSprite.anchor.set(0.5);
      vfxSprite.x = x;
      vfxSprite.y = y - 10; // Slightly above the pokemon
      vfxSprite.scale.set(0.8);
      vfxSprite.animationSpeed = 0.5;
      vfxSprite.loop = false;
      vfxSprite.zIndex = 100;

      vfxSprite.onComplete = () => {
        if (!vfxSprite.destroyed) {
          vfxSprite.destroy();
        }
      };

      this.playerPokemonContainer.addChild(vfxSprite);
      vfxSprite.play();
    } catch {
      // VFX failed silently
    }
  }

  /**
   * Legacy method - calls updatePlayerPokemon with no slot info.
   * @deprecated Use updatePlayerPokemon instead
   */
  async spawnPlayerPokemon(pokemons: RaidPokemon[]): Promise<void> {
    // Convert to PokemonWithSlot format (index = array position)
    const pokemonsWithSlots: PokemonWithSlot[] = pokemons.map((pokemon, i) => ({
      pokemon,
      slotIndex: i,
    }));
    await this.updatePlayerPokemon(pokemonsWithSlots, pokemons.length);
  }

  /**
   * Clear all player Pokemon from the section
   */
  clearPlayerPokemon(): void {
    for (const pokemon of this.playerPokemons) {
      try {
        const container = (pokemon as any).container;
        if (container && !container.destroyed) {
          container.destroy({ children: true });
        }
      } catch {
        // Cleanup failed silently
      }
    }
    this.playerPokemons = [];
    this.playerPokemonBySlot.clear();
  }

  /**
   * Update slot placeholders (ellipses with labels) at positions where pokemon will spawn.
   * Shows empty slots with player names or "SHARED" label.
   */
  updateSlotPlaceholders(
    slots: SlotConfig[],
    players: Record<PlayerRole, { username: string } | null>,
  ): void {
    if (this.isDestroyed) return;

    // Clear existing placeholders
    this.slotPlaceholderContainer.removeChildren();

    if (slots.length === 0) return;

    // Position container same as playerPokemonContainer
    const battleScale = this.getBattleScale();
    const mapPos = this.getMapPosition();
    this.slotPlaceholderContainer.scale.set(battleScale);
    this.slotPlaceholderContainer.x = mapPos.x;
    this.slotPlaceholderContainer.y = this.bounds.height * 0.65;

    // Same spacing as player pokemon
    const spacingX = 60;
    const totalWidth = (slots.length - 1) * spacingX;
    const startX = -totalWidth / 2;

    slots.forEach((slot, i) => {
      // Create container for each slot placeholder
      const placeholderGroup = new Container();
      placeholderGroup.x = startX + i * spacingX;
      placeholderGroup.y = 0;
      placeholderGroup.zIndex = -10;

      // Draw ellipse (black, 50% opacity)
      const ellipse = new Graphics();
      ellipse.ellipse(0, 10, 24, 8);
      ellipse.fill({ color: 0x77b982, alpha: 0.35 });
      placeholderGroup.addChild(ellipse);

      // Determine label text
      let labelText = "SHARED";
      if (slot.type === "personal" && slot.assigned_to) {
        const player = players[slot.assigned_to];
        labelText =
          player?.username ?? slot.assigned_to.replace("player_", "P");
      }

      // Create text label
      const label = new Text({
        text: labelText,
        style: {
          fontFamily: "vck",
          fontSize: 14,
          fill: 0xffffff,
          align: "center",
          stroke: { color: 0x000000, width: 5 },
        },
      });
      label.anchor.set(0.5, 0);
      label.y = 20;
      placeholderGroup.addChild(label);

      this.slotPlaceholderContainer.addChild(placeholderGroup);
    });
  }

  /**
   * Clear all slot placeholders from the section
   */
  clearSlotPlaceholders(): void {
    this.slotPlaceholderContainer.removeChildren();
  }

  /**
   * Remove the mask to show full map (used during expansion)
   */
  removeMask(): void {
    this.container.mask = null;
    // Also remove the mask graphics from the container's children
    // (it was added as a child in applyMask)
    if (this.mask && this.container.children.includes(this.mask)) {
      this.container.removeChild(this.mask);
    }
  }

  /**
   * Switch to battle position mode.
   * This repositions the map from preview (top-aligned) to battle (grid-centered).
   */
  switchToBattleMode(): void {
    this.positionMode = "battle";

    // Reposition map to battle position
    if (this.mapSprite) {
      const mapPos = this.getBattleMapPosition();
      this.mapSprite.x = mapPos.x;
      this.mapSprite.y = mapPos.y;
    }

    // Reposition pokemon containers to match
    const mapPos = this.getBattleMapPosition();
    this.pokemonContainer.x = mapPos.x;
    this.pokemonContainer.y = mapPos.y;

    this.playerPokemonContainer.x = mapPos.x;
    this.playerPokemonContainer.y = this.bounds.height * 0.65;

    // Reposition slot placeholder container
    this.slotPlaceholderContainer.x = mapPos.x;
    this.slotPlaceholderContainer.y = this.bounds.height * 0.65;
  }

  /**
   * Update bounds during expansion animation.
   * Only updates the mask size - map scale and position stay constant.
   */
  updateBounds(newBounds: RoomSectionBounds): void {
    this.bounds = newBounds;
    this.container.x = newBounds.x;
    this.container.y = newBounds.y;

    // Update mask to reveal more of the map
    this.drawMask();

    // Reposition map in section-local coordinates
    // (map scale stays constant, only Y offset changes as section moves)
    if (this.mapSprite) {
      const mapPos = this.getMapPosition();
      this.mapSprite.x = mapPos.x;
      this.mapSprite.y = mapPos.y;
      // Scale stays constant - no rescaling during expansion
    }

    // Reposition pokemon container to match map center
    const mapPos = this.getMapPosition();
    this.pokemonContainer.x = mapPos.x;
    this.pokemonContainer.y = mapPos.y;

    // Reposition each enemy Pokemon in a centered row
    // Note: pokemonContainer is already scaled, so use unscaled spacing
    const spacingX = 60;
    const totalWidth = (this.pokemons.length - 1) * spacingX;
    const startX = -totalWidth / 2;

    this.pokemons.forEach((pokemon, i) => {
      const pokemonContainer = (pokemon as any).container;
      if (pokemonContainer && !pokemonContainer.destroyed) {
        pokemonContainer.x = startX + i * spacingX;
        pokemonContainer.y = 0;
      }
    });

    // Reposition player pokemon container (at bottom portion of visible area)
    this.playerPokemonContainer.x = mapPos.x;
    this.playerPokemonContainer.y = newBounds.height * 0.65;

    // Reposition slot placeholder container (same position as player pokemon)
    this.slotPlaceholderContainer.x = mapPos.x;
    this.slotPlaceholderContainer.y = newBounds.height * 0.65;

    // Reposition each player Pokemon by their slot index (not array index)
    const playerSpacingX = 60;
    const playerTotalWidth = (this.totalSlots - 1) * playerSpacingX;
    const playerStartX = -playerTotalWidth / 2;

    for (const [slotIndex, { pokemon }] of this.playerPokemonBySlot) {
      const pokemonContainer = (pokemon as any).container;
      if (pokemonContainer && !pokemonContainer.destroyed) {
        pokemonContainer.x = playerStartX + slotIndex * playerSpacingX;
        pokemonContainer.y = 0;
      }
    }
  }

  /**
   * Prepares the section for battle replay by updating pokemon with real battle data.
   * Clears UI cruft and returns a mapping of pokemon keyed by battle log IDs.
   *
   * This enables scene continuity: draft pokemon are updated in-place
   * with real battle stats, avoiding asset recreation.
   *
   * @param battleLog - The complete battle log from the server
   * @param world - The world container to reparent content to
   * @returns Pokemon record keyed by battle log IDs, ready for BattleReplay
   */
  prepareForBattle(
    battleLog: CompleteBattleLog,
    world: Container,
  ): {
    pokemons: Record<string, ClientPokemon>;
    mapSprite: Sprite | null;
  } {
    // Clear slot placeholders (draft UI)
    this.clearSlotPlaceholders();

    // Build pokemon record keyed by battle log IDs
    const pokemonRecord: Record<string, ClientPokemon> = {};

    // Match TeamB (enemies) from battle log to existing enemy pokemon
    // Strategy: match by array index since spawn order is consistent
    const teamBMembers = battleLog.teamB.members;
    for (let i = 0; i < teamBMembers.length && i < this.pokemons.length; i++) {
      const member = teamBMembers[i];
      const existingPokemon = this.pokemons[i];

      if (member && existingPokemon) {
        // Update the pokemon with real battle data
        existingPokemon.updateBattleData(
          member.battleData,
          member.initialPosition,
        );

        // Add to record with the real ID from battle log
        pokemonRecord[member.pokemon.id.toString()] = existingPokemon;
      }
    }

    // Match TeamA (player pokemon) from battle log to existing player pokemon
    // Strategy: match by pokemon ID (array order is NOT guaranteed by backend)
    const teamAMembers = battleLog.teamA.members;

    for (const member of teamAMembers) {
      // Find the existing visual pokemon by matching its stored pokemonId
      let matchedEntry: { pokemon: ClientPokemon; pokemonId: number } | null =
        null;

      for (const [, entry] of this.playerPokemonBySlot) {
        if (entry.pokemonId === member.pokemon.id) {
          matchedEntry = entry;
          break;
        }
      }

      if (matchedEntry) {
        // Update the pokemon with real battle data
        matchedEntry.pokemon.updateBattleData(
          member.battleData,
          member.initialPosition,
        );

        // Add to record with the real ID from battle log
        pokemonRecord[member.pokemon.id.toString()] = matchedEntry.pokemon;
      }
    }

    // Reparent map and pokemon containers to world
    // First, detach from section container
    if (this.mapSprite && !this.mapSprite.destroyed) {
      this.container.removeChild(this.mapSprite);
      world.addChild(this.mapSprite);

      // Reposition map for battle (grid-centered in world coordinates)
      const battleScale = this.getBattleScale();
      this.mapSprite.x = BATTLE_MAP_CENTER_X;
      this.mapSprite.y = BATTLE_MAP_CENTER_Y;
      this.mapSprite.scale.set(BATTLE_MAP_SCALE);
    }

    // Reparent pokemon containers to world
    this.container.removeChild(this.pokemonContainer);
    world.addChild(this.pokemonContainer);

    this.container.removeChild(this.playerPokemonContainer);
    world.addChild(this.playerPokemonContainer);

    // Reset container transforms for world coordinates
    // Pokemon are now positioned via updateBattleData in grid coordinates
    this.pokemonContainer.scale.set(1);
    this.pokemonContainer.x = 0;
    this.pokemonContainer.y = 0;

    this.playerPokemonContainer.scale.set(1);
    this.playerPokemonContainer.x = 0;
    this.playerPokemonContainer.y = 0;

    console.log(
      `[RoomSection.prepareForBattle] Prepared ${
        Object.keys(pokemonRecord).length
      } pokemon for battle`,
    );

    return {
      pokemons: pokemonRecord,
      mapSprite: this.mapSprite,
    };
  }

  /** Active targeting indicators by pokemon ID */
  private targetingIndicators: Map<number, Graphics> = new Map();

  /**
   * Show targeting indicators on valid target pokemon.
   * @param validTargetIds Array of pokemon IDs that are valid targets
   */
  showTargetingIndicators(validTargetIds: number[]): void {
    if (this.isDestroyed) return;

    // Clear existing indicators first
    this.clearTargetingIndicators();

    // Add indicators to valid targets
    for (const [, entry] of this.playerPokemonBySlot) {
      if (validTargetIds.includes(entry.pokemonId)) {
        const container = (entry.pokemon as any).container as Container;
        if (container && !container.destroyed) {
          const indicator = this.createTargetingIndicator();
          container.addChild(indicator);
          this.targetingIndicators.set(entry.pokemonId, indicator);
        }
      }
    }
  }

  /**
   * Clear all targeting indicators
   */
  clearTargetingIndicators(): void {
    for (const [, indicator] of this.targetingIndicators) {
      if (!indicator.destroyed) {
        indicator.destroy();
      }
    }
    this.targetingIndicators.clear();
  }

  /**
   * Create a pulsing ring targeting indicator
   */
  private createTargetingIndicator(): Graphics {
    const indicator = new Graphics();
    indicator.zIndex = 500;

    // Draw pulsing ellipse at pokemon feet
    const drawRing = (scale: number, alpha: number) => {
      indicator.clear();
      indicator.ellipse(0, 12, 28 * scale, 10 * scale);
      indicator.stroke({ color: 0x4ade80, width: 3, alpha });
      indicator.ellipse(0, 12, 22 * scale, 8 * scale);
      indicator.stroke({ color: 0xffffff, width: 2, alpha: alpha * 0.7 });
    };

    // Animate the indicator
    let time = 0;
    const animate = () => {
      if (indicator.destroyed) return;
      time += 0.05;
      const pulse = 0.9 + Math.sin(time * 3) * 0.1;
      const alpha = 0.7 + Math.sin(time * 2) * 0.3;
      drawRing(pulse, alpha);
      requestAnimationFrame(animate);
    };

    drawRing(1, 1);
    requestAnimationFrame(animate);

    return indicator;
  }

  /**
   * Get pokemon ID at screen position (for click/drop detection)
   * @param screenX Screen X coordinate
   * @param screenY Screen Y coordinate
   * @param validTargetIds Only consider these pokemon IDs as valid
   * @returns Pokemon ID if found, null otherwise
   */
  getPokemonAtPosition(
    screenX: number,
    screenY: number,
    validTargetIds: number[],
  ): number | null {
    if (this.isDestroyed) return null;

    // Check player pokemon
    for (const [, entry] of this.playerPokemonBySlot) {
      if (!validTargetIds.includes(entry.pokemonId)) continue;

      const container = (entry.pokemon as any).container as Container;
      if (!container || container.destroyed) continue;

      // Get global bounds of the pokemon container
      const bounds = container.getBounds();

      // Check if point is within bounds (with some padding for easier targeting)
      const padding = 15;
      if (
        screenX >= bounds.x - padding &&
        screenX <= bounds.x + bounds.width + padding &&
        screenY >= bounds.y - padding &&
        screenY <= bounds.y + bounds.height + padding
      ) {
        return entry.pokemonId;
      }
    }

    return null;
  }

  /**
   * Destroy this section and all its resources
   */
  destroy(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Clear targeting indicators
    this.clearTargetingIndicators();

    // Destroy all enemy Pokemon
    for (const pokemon of this.pokemons) {
      try {
        const container = (pokemon as any).container;
        if (container && !container.destroyed) {
          container.destroy({ children: true });
        }
      } catch {
        // Cleanup failed silently
      }
    }
    this.pokemons = [];

    // Destroy all player Pokemon
    this.clearPlayerPokemon();

    // Destroy map sprite
    if (this.mapSprite && !this.mapSprite.destroyed) {
      this.mapSprite.destroy();
      this.mapSprite = null;
    }

    // Destroy containers
    if (this.pokemonContainer && !this.pokemonContainer.destroyed) {
      this.pokemonContainer.destroy({ children: true });
    }
    if (this.playerPokemonContainer && !this.playerPokemonContainer.destroyed) {
      this.playerPokemonContainer.destroy({ children: true });
    }
    if (
      this.slotPlaceholderContainer &&
      !this.slotPlaceholderContainer.destroyed
    ) {
      this.slotPlaceholderContainer.destroy({ children: true });
    }
    if (this.container && !this.container.destroyed) {
      this.container.destroy({ children: true });
    }
  }

  /**
   * Apply item effect visual feedback to a player pokemon.
   * Shows healing VFX, floating heal text, item icon, and syncs HP bar.
   *
   * @param targetPokemonId - The pokemon ID to apply the effect to
   * @param itemName - The item name (e.g., "potion", "super-potion")
   * @param healAmount - The amount healed (for floating text)
   * @param newHp - The new HP value after healing (for HP bar sync)
   */
  async applyItemEffect(
    targetPokemonId: number,
    itemName: string,
    healAmount: number,
    newHp: number,
  ): Promise<void> {
    if (this.isDestroyed) return;

    // Find the target pokemon by ID
    let targetPokemon: ClientPokemon | null = null;
    let pokemonContainer: Container | null = null;

    for (const [, entry] of this.playerPokemonBySlot) {
      if (entry.pokemonId === targetPokemonId) {
        targetPokemon = entry.pokemon;
        pokemonContainer = (entry.pokemon as any).container;
        break;
      }
    }

    if (!targetPokemon || !pokemonContainer || pokemonContainer.destroyed) {
      console.warn(
        `[RoomSection] Could not find pokemon ${targetPokemonId} for item effect`,
      );
      return;
    }

    // 1. Sync HP bar
    targetPokemon.setHP(newHp);

    // 2. Show floating heal text
    targetPokemon.showFloatingHeal(healAmount);

    // 3. Play healing VFX
    await this.playHealVFX(pokemonContainer);

    // 4. Show item icon above pokemon
    await this.showItemIcon(pokemonContainer, itemName);
  }

  /**
   * Play healing sparkle VFX on a pokemon container.
   */
  private async playHealVFX(pokemonContainer: Container): Promise<void> {
    try {
      const texture = Cache.has(HEAL_VFX_PATH)
        ? Cache.get(HEAL_VFX_PATH)
        : await Assets.load<Texture>(HEAL_VFX_PATH);

      const frameCount = VFX_SPRITE_FRAME_COUNT_DATA[HEAL_VFX_PATH];
      if (!frameCount) return;

      const frames: Texture[] = [];
      const frameWidth = texture.width / frameCount;
      const frameHeight = texture.height;

      for (let i = 0; i < frameCount; i++) {
        const cacheKey = HEAL_VFX_PATH + "__frame-" + i;
        if (Cache.has(cacheKey)) {
          frames.push(Cache.get(cacheKey));
        } else {
          const frame = new Texture({
            source: texture.source,
            frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight),
          });
          Cache.set(cacheKey, frame);
          frames.push(frame);
        }
      }

      const vfxSprite = new AnimatedSprite(frames);
      vfxSprite.anchor.set(0.5);
      vfxSprite.x = 0;
      vfxSprite.y = 0;
      vfxSprite.scale.set(1.2);
      vfxSprite.animationSpeed = 0.6;
      vfxSprite.loop = false;
      vfxSprite.zIndex = 200;

      vfxSprite.onComplete = () => {
        if (!vfxSprite.destroyed) {
          vfxSprite.destroy();
        }
      };

      pokemonContainer.addChild(vfxSprite);
      vfxSprite.play();
    } catch {
      // VFX failed silently
    }
  }

  /**
   * Show item icon above pokemon with pop-in, float-up, fade-out animation.
   */
  private async showItemIcon(
    pokemonContainer: Container,
    itemName: string,
  ): Promise<void> {
    try {
      const itemPath = `/assets/items/${itemName}.png`;
      const texture = Cache.has(itemPath)
        ? Cache.get(itemPath)
        : await Assets.load<Texture>(itemPath);

      // Create circular background
      const iconContainer = new Container();
      iconContainer.x = 0;
      iconContainer.y = -60; // Start above pokemon
      iconContainer.zIndex = 300;

      // White circle background
      const circle = new Graphics();
      circle.circle(0, 0, 18);
      circle.fill({ color: 0xffffff, alpha: 0.95 });
      circle.stroke({ color: 0x4ade80, width: 3 }); // Green border
      iconContainer.addChild(circle);

      // Item sprite
      const itemSprite = new Sprite(texture);
      itemSprite.anchor.set(0.5);
      itemSprite.width = 24;
      itemSprite.height = 24;
      iconContainer.addChild(itemSprite);

      pokemonContainer.addChild(iconContainer);

      // Animation: pop-in, float-up, fade-out
      const duration = 1500;
      const startTime = performance.now();
      const startY = iconContainer.y;
      const endY = startY - 40;

      const animate = (now: number) => {
        if (iconContainer.destroyed) return;

        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Pop-in scale (elastic ease-out in first 20%)
        let scale: number;
        if (progress < 0.2) {
          const t = progress / 0.2;
          // Elastic pop: 0 -> 1.3 -> 1.0
          if (t < 0.6) {
            scale = (t / 0.6) * 1.3;
          } else {
            scale = 1.3 - ((t - 0.6) / 0.4) * 0.3;
          }
        } else {
          scale = 1;
        }
        iconContainer.scale.set(scale);

        // Float up (linear)
        iconContainer.y = startY + (endY - startY) * progress;

        // Fade out (last 40%)
        if (progress > 0.6) {
          iconContainer.alpha = 1 - (progress - 0.6) / 0.4;
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          iconContainer.destroy({ children: true });
        }
      };

      requestAnimationFrame(animate);
    } catch {
      // Item icon failed silently
    }
  }
}
